// frontend/src/hooks/useReceiverEvents.js
import i18n from '../i18n/config';
import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const normalizeAssignedReceiverStatus = (status) => {
  if (!status) return 'offline';
  return status === 'sin_receptor' ? 'offline' : status;
};

/**
 * Hook para obtener eventos/historial de un receptor
 * Determina automáticamente el estado basado en el último evento online
 */
export const useReceiverEvents = (receiverId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Datos formateados para fácil acceso
  const [status, setStatus] = useState(null); // 'online', 'warning', 'offline' (y 'sin_receptor' solo cuando aplica)
  const [lastOnline, setLastOnline] = useState(null);
  const [events, setEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    if (!receiverId) {
      setData(null);
      setStatus(null);
      setLastOnline(null);
      setEvents([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.receivers.getEvents(receiverId);
      const normalizedStatus = normalizeAssignedReceiverStatus(response.data?.status);
      const normalizedData = {
        ...(response.data || {}),
        status: normalizedStatus
      };

      setData(normalizedData);
      setStatus(normalizedStatus);
      setLastOnline(normalizedData.last_online);
      setEvents(normalizedData.events || []);
    } catch (err) {
      console.error('Error fetching receiver events:', err);
      setError(err.response?.data?.detail || err.message);
      setData(null);
      setStatus(null);
      setLastOnline(null);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [receiverId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    data,
    loading,
    error,
    status,
    lastOnline,
    events,
    refetch: fetchEvents
  };
};

/**
 * Hook para obtener eventos de múltiples receptores
 * Útil cuando se necesita información de varios receptores del vehículo
 */
export const useReceiverEventsMultiple = (receiverIds = []) => {
  const [eventsMap, setEventsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAllEvents = useCallback(async () => {
    if (!receiverIds || receiverIds.length === 0) {
      setEventsMap({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const promises = receiverIds.map(id => api.receivers.getEvents(id));
      const responses = await Promise.all(promises);
      
      const newMap = {};
      responses.forEach((response, idx) => {
        if (response.data) {
          newMap[receiverIds[idx]] = {
            ...response.data,
            status: normalizeAssignedReceiverStatus(response.data.status)
          };
        }
      });
      
      setEventsMap(newMap);
    } catch (err) {
      console.error('Error fetching multiple receiver events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [receiverIds]);

  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  return {
    eventsMap,
    loading,
    error,
    refetch: fetchAllEvents
  };
};

/**
 * Formatea la fecha/hora para mostrar en UI
 */
export const formatReceiverEventTime = (isoTimestamp, options = {}) => {
  if (!isoTimestamp) return 'Sin datos';

  try {
    const date = new Date(isoTimestamp);
    const formatter = new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      ...options
    });

    return formatter.format(date);
  } catch (err) {
    console.error('Error formatting timestamp:', err);
    return isoTimestamp;
  }
};

/**
 * Calcula el tiempo transcurrido desde el último evento
 * Retorna un string descriptivo como "2 horas", "3 días", etc
 */
export const getTimeSinceLastOnline = (isoTimestamp) => {
  if (!isoTimestamp) return null;

  try {
    const lastOnlineDate = new Date(isoTimestamp);
    const now = new Date();
    const diffMs = now - lastOnlineDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return i18n.t('hardcoded.hooks_usereceiverevents.hace_menos_de_1_minuto');
    if (diffMinutes < 60) return i18n.t('hardcoded.hooks_usereceiverevents.hace_minutos', { minutes: diffMinutes });
    if (diffHours < 24) return i18n.t('hardcoded.hooks_usereceiverevents.hace_horas', { hours: diffHours });
    if (diffDays < 365) return i18n.t('hardcoded.hooks_usereceiverevents.hace_dias', { days: diffDays });

    return i18n.t('hardcoded.hooks_usereceiverevents.mas_de_un_anio');
  } catch (err) {
    console.error('Error calculating time since last online:', err);
    return null;
  }
};
